import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

const AnomalyExceptionDialog = ({
  open,
  onClose,
  row,
  sourceKey,
  categoryKey,
  firebaseUser,
  onSaved,
}) => {
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState(null);

  const hasException = !!row?.exception;

  const handleSave = async () => {
    if (!comment.trim()) return;
    try {
      setSaving(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      // Determine anomaly type
      let anomalyType = 'store';
      if (row.isMissing || row.rowCount === 0) {
        anomalyType = 'missing';
      } else if (row.isDayOfWeekAnomaly) {
        anomalyType = 'day-of-week';
      }

      await axios.post(
        '/hd-api/data-integrity-2/exceptions',
        {
          sourceKey,
          categoryKey,
          businessDate: row.businessDate,
          storeId: row.storeId || 'ALL',
          anomalyType,
          comment: comment.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setComment('');
      setSaving(false);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving exception:', err);
      setError('Failed to save exception.');
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!row?.exception?.id) return;
    try {
      setRevoking(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      await axios.delete(
        `/hd-api/data-integrity-2/exceptions/${row.exception.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setRevoking(false);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Error revoking exception:', err);
      setError('Failed to revoke exception.');
      setRevoking(false);
    }
  };

  const handleClose = () => {
    setComment('');
    setError(null);
    onClose();
  };

  if (!row) return null;

  // Determine what type of issue this is
  let issueLabel = 'Store Anomaly';
  if (row.isMissing || row.rowCount === 0) {
    issueLabel = 'Missing Data';
  } else if (row.isDayOfWeekAnomaly) {
    issueLabel = 'Day-of-Week Anomaly';
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Lato', fontWeight: 600 }}>
        {hasException ? 'Exception Details' : 'Acknowledge Exception'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Date</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.businessDate}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Store</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.storeId}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Type</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{issueLabel}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Row Count</Typography>
              <Typography variant="body2">{row.rowCount?.toLocaleString()}</Typography>
            </Box>
            {row.isAnomaly && row.storeMedian != null && (
              <Box>
                <Typography variant="caption" color="text.secondary">Store Median</Typography>
                <Typography variant="body2">{row.storeMedian?.toLocaleString()}</Typography>
              </Box>
            )}
            {row.isDayOfWeekAnomaly && row.dayOfWeekAvg != null && (
              <Box>
                <Typography variant="caption" color="text.secondary">Day-of-Week Avg</Typography>
                <Typography variant="body2">{row.dayOfWeekAvg?.toLocaleString()}</Typography>
              </Box>
            )}
          </Box>

          {hasException ? (
            <Box sx={{ mt: 1, p: 2, backgroundColor: 'rgba(46,125,50,0.06)', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Exception Comment</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{row.exception.comment}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Acknowledged by {row.exception.acknowledgedBy}
                {row.exception.acknowledgedAt && ` on ${new Date(row.exception.acknowledgedAt).toLocaleString()}`}
              </Typography>
            </Box>
          ) : (
            <TextField
              label="Comment"
              placeholder="Explain why this anomaly is expected..."
              multiline
              minRows={2}
              maxRows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              required
              sx={{ mt: 1 }}
            />
          )}

          {error && (
            <Typography variant="body2" color="error">{error}</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        {hasException ? (
          <Button
            onClick={handleRevoke}
            color="error"
            variant="outlined"
            disabled={revoking}
            startIcon={revoking ? <CircularProgress size={16} /> : null}
          >
            Revoke Exception
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !comment.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            Acknowledge
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AnomalyExceptionDialog;
